import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import _ from 'underscore';
import { toast } from 'react-toastify';
import CreatableSelect from 'react-select/creatable';

import YesNoModal from 'components/Modals/yesNoModal';
import Dropdown from 'components/Dropdown/Dropdown';
import MultichoiceCreatorQuillEditor from './MultichoiceCreatorQuillEditor';
import { Button } from 'components/Button/RoundButton';
import { useMultichoiceCreatorContext } from 'hooks/useContext';
import { useDeleteQuestionOptionAndParagraph } from './useDeleteQuestionOptionAndParagraph';
import services from 'api/services';
import { RectangleButton } from 'components/Button/RectangleButton';
import {
  ButtonContainer,
  FieldContainer,
  InputField,
  QuestionsContainer,
  QuestionsSelectionButton,
  ITag,
  AddQuestionButton,
  DetailsLabel,
  QuestionDetailsContainer,
  EmptyContainer,
  Label,
  DescriptionContainer,
  OptionsContainer,
  OptionDetailsContainer,
  ParagraphContainer,
  CategorySelectContainer,
} from './styles';
import { Spinner } from 'components/Spinner/Spinner';
import { questionTypes } from 'constants/tests';

const { TestService } = services;

export default React.memo(() => {
  const navigate = useNavigate();
  const { testId, questionId } = useParams();

  const questionTypesLabels = {
    oneCorrectAnswer: { label: 'One Correct Answer', value: questionTypes.ONE_CORRECT_ANSWER },
    freeText: { label: 'Free Text', value: questionTypes.FREE_TEXT },
  };

  const selectCategoryRef = useRef();
  const selectQuestionTypeRef = useRef();

  const { test, question, setQuestion, questions, options, setOptions, paragraph, setParagraph, paragraphs } =
    useMultichoiceCreatorContext();
  const [deleting, deleteQuestionOptionAndParagraph] = useDeleteQuestionOptionAndParagraph();

  const [deleteModalInfo, setDeleteModalInfo] = useState(undefined);
  const [version, setVersion] = useState(0);
  const [isTestEdited, setIsTestEdited] = useState(false);
  const [isSavingQuestion, setIsSavingQuestion] = useState(false);

  const useBeforeUnload = (isTestEdited) => {
    useEffect(() => {
      const handleBeforeUnload = (event) => {
        event.preventDefault();
        event.returnValue = '';
        return '';
      };
      if (isTestEdited) {
        window.addEventListener('beforeunload', handleBeforeUnload);
      }
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isTestEdited]);
  };

  useBeforeUnload(isTestEdited);

  const getColor = (q) => 'hsl(' + ((q.correct / q.totalAnswered) * 100).toFixed(0) + ', 100%, 45%)';

  const categoriesOptions = useMemo(() => {
    return (questions || [])
      .reduce((memo, question) => {
        if (question.category && !memo.includes(question.category)) memo.push(question.category);
        return memo;
      }, [])
      .map((category) => ({ label: category, value: category }));
  }, [questions]);

  const renderCategorySection = () => {
    return (
      <CategorySelectContainer>
        <Label>Category</Label>
        <CreatableSelect
          ref={selectCategoryRef}
          placeholder="Category name"
          isDisabled={test?.disabledEditing}
          options={test?.disabledEditing ? [] : categoriesOptions}
          isSearchable={true}
          isClearable={true}
          value={question.category ? { label: question.category, value: question.category } : undefined}
          onChange={(response) => setQuestion({ ...question, category: response?.value ?? '' })}
          onCreateOption={(newCategory) => setQuestion({ ...question, category: newCategory })}
        />
      </CategorySelectContainer>
    );
  };

  const renderQuestionTypeSection = () => {
    return (
      <CategorySelectContainer>
        <Label>Question Type</Label>
        <CreatableSelect
          ref={selectQuestionTypeRef}
          placeholder="Question type"
          isDisabled={test?.disabledEditing}
          options={test?.disabledEditing ? [] : Object.values(questionTypesLabels)}
          isSearchable={true}
          isClearable={false}
          value={question.questionType ? questionTypesLabels[question.questionType] : undefined}
          onChange={(response) => {
            setQuestion({ ...question, questionType: response?.value ?? '' });
          }}
        />
      </CategorySelectContainer>
    );
  };

  const renderParagraphSection = () => {
    let paragraphsForDropdown = paragraphs?.map((paragraph) => {
      return { ...paragraph, label: paragraph.name };
    });
    return (
      <>
        <ButtonContainer alignItems="baseline" margin="0 0 10px">
          <RectangleButton small onClick={() => setParagraph({ new: true, name: '', value: '' })}>
            <ITag className="fa fa-plus " color="white" />
            Add Paragraph
          </RectangleButton>
        </ButtonContainer>
        <ButtonContainer>
          <Dropdown
            width="270px"
            options={test?.disabledEditing ? [] : paragraphsForDropdown}
            value={paragraph._id ? paragraph.name : 'No paragraph selected'}
            onChange={(response) => setParagraph(response)}
          />
          {paragraph._id && !test?.disabledEditing && (
            <ITag
              className="fa fa-trash "
              onClick={() => {
                setDeleteModalInfo({
                  title: 'Confirmation',
                  body: 'Do you want to remove the paragraph from the question?',
                  id: question._id,
                  type: 'paragraph',
                });
              }}
            />
          )}
        </ButtonContainer>
        {(paragraph.new || paragraph._id) && (
          <ParagraphContainer>
            <FieldContainer>
              <InputField
                disabled={test?.disabledEditing}
                type="text"
                placeholder="Paragraph name"
                margin="0 0 15px"
                width="270px"
                value={paragraph.name}
                onChange={(e) => {
                  setParagraph({ ...paragraph, name: e.target.value });
                }}
              />
              <MultichoiceCreatorQuillEditor
                key={'quill-paragraph'}
                id="quill-paragraph"
                value={paragraph.value}
                placeholder="Paragraph"
                disabled={test?.disabledEditing}
                height="250px"
                isParagraph
                onChange={(updatedValue, delta, source) => {
                  if (source === 'user') {
                    setIsTestEdited(true);
                    setParagraph({ ...paragraph, value: updatedValue });
                  }
                }}
              />
            </FieldContainer>
          </ParagraphContainer>
        )}
      </>
    );
  };

  const renderQuestionAndOptions = () => {
    return (
      <DescriptionContainer key={version}>
        <EmptyContainer>
          <Label>Question</Label>
          <MultichoiceCreatorQuillEditor
            key={`quill-question-${question._id ?? 'new'}`}
            id="quill-question"
            value={question.value}
            placeholder="Question"
            disabled={test?.disabledEditing}
            minHeight="200px"
            maxHeight="300px"
            onDelete={() => {
              if (!question._id) {
                setParagraph(paragraph ?? {});
                setQuestion(question ?? {});
                return setOptions(options ?? []);
              }
              setDeleteModalInfo({
                title: 'Confirmation',
                body: 'Do you want to delete the question?',
                id: question._id,
                type: 'question',
              });
            }}
            onChange={(updatedValue, delta, source) => {
              if (source !== 'api') {
                setIsTestEdited(true);
                setQuestion({ ...question, value: updatedValue });
              }
            }}
          />
          <Label margin="15px 0">Comment</Label>
          <MultichoiceCreatorQuillEditor
            key={'quill-comment'}
            id="quill-comment"
            value={question.comment}
            placeholder="Comment"
            disabled={test?.disabledEditing}
            minHeight="50px"
            maxHeight="150px"
            isComment
            onChange={(updatedValue, delta, source) => {
              if (source !== 'api') {
                setIsTestEdited(true);
                setQuestion({ ...question, comment: updatedValue });
              }
            }}
          />
        </EmptyContainer>
        <EmptyContainer>
          <Label>Options</Label>
          {question.questionType === 'freeText' ? (
            <MultichoiceCreatorQuillEditor
              key={'quill-freeText'}
              id="quill-freeText"
              placeholder="The candidate will type their answer here"
              disabled={true}
              minHeight="150px"
              maxHeight="350px"
              isComment
            />
          ) : (
            options?.map((option, index) => {
              if (option.archived) return false;
              return (
                <OptionsContainer key={option._id ?? index}>
                  {option._id && (
                    <OptionDetailsContainer>
                      <EmptyContainer>
                        Created: {moment(option.createdAt).fromNow()}, Edited: {moment(option.editedAt).fromNow()}
                      </EmptyContainer>
                      <EmptyContainer>
                        Answered:{' '}
                        {option.totalAnswered
                          ? ((option.totalAnswered / question.totalAnswered) * 100).toFixed(2)
                          : '0.00'}
                        %
                      </EmptyContainer>
                    </OptionDetailsContainer>
                  )}
                  <MultichoiceCreatorQuillEditor
                    key={`quill-option-${option._id ?? index}`}
                    id={`quill-option-${index}`}
                    value={option.value}
                    placeholder="Option"
                    disabled={test?.disabledEditing}
                    minHeight="100px"
                    maxHeight="300px"
                    isCorrectOption={option.correct}
                    isIncorrectOption={!option.correct}
                    onOptionAnswerChange={(value) => {
                      let copyOfOptions = JSON.parse(JSON.stringify(options));
                      copyOfOptions.map((o, subIndex) => {
                        if (o._id && option._id && o._id === option._id) o.correct = value;
                        else if (subIndex === index) o.correct = value;
                        else o.correct = false;
                        return o;
                      });
                      setIsTestEdited(true);
                      setOptions(copyOfOptions);
                      setVersion((v) => {
                        return v + 1;
                      });
                    }}
                    onDelete={() => {
                      if (!option._id) {
                        let copyOfOptions = JSON.parse(JSON.stringify(options));
                        copyOfOptions.splice(index, 1);
                        return setOptions(copyOfOptions);
                      }
                      setDeleteModalInfo({
                        title: 'Confirmation',
                        body: 'Do you want to delete the option?',
                        id: option._id,
                        type: 'option',
                      });
                    }}
                    onChange={(updatedValue, delta, source) => {
                      if (source === 'api') return;
                      let copyOfOptions = JSON.parse(JSON.stringify(options));
                      copyOfOptions[index].value = updatedValue;
                      setOptions(copyOfOptions);
                      setIsTestEdited(true);
                    }}
                  />
                </OptionsContainer>
              );
            })
          )}
        </EmptyContainer>
      </DescriptionContainer>
    );
  };

  const renderQuestionDetails = () => (
    <QuestionDetailsContainer>
      {question.totalAnswered && (
        <>
          <EmptyContainer>
            <DetailsLabel isHeading>Total Attempted:</DetailsLabel>
            <DetailsLabel>&nbsp;{question.totalAnswered}</DetailsLabel>
          </EmptyContainer>
          <EmptyContainer>
            <DetailsLabel isHeading>Answered correctly:</DetailsLabel>
            <DetailsLabel>&nbsp;{((question.correct / question.totalAnswered) * 100).toFixed(2)}%</DetailsLabel>
          </EmptyContainer>
        </>
      )}
      {test?.adaptiveTest && question.rating && (
        <EmptyContainer>
          <DetailsLabel isHeading>Elo Rating:</DetailsLabel>
          <DetailsLabel>&nbsp;{question.rating}</DetailsLabel>
        </EmptyContainer>
      )}
      <EmptyContainer>
        <DetailsLabel isHeading>Created At:</DetailsLabel>
        <DetailsLabel>&nbsp;{moment(question.createdAt).fromNow()}</DetailsLabel>
      </EmptyContainer>
      <EmptyContainer>
        <DetailsLabel isHeading>Edited At:</DetailsLabel>
        <DetailsLabel>&nbsp;{moment(question.editedAt).fromNow()}</DetailsLabel>
      </EmptyContainer>
    </QuestionDetailsContainer>
  );

  const saveQuestionOptionsAndParagraph = async () => {
    try {
      if (isSavingQuestion) toast.error('Already saving');
      setIsSavingQuestion(true);
      if (question.questionType === questionTypes.ONE_CORRECT_ANSWER && options?.length < 4)
        toast.warning('It is recommended that questions have at least four alternatives.');
      let requestBody = {
        question,
        options,
        paragraph,
      };
      if (question.questionType === questionTypes.FREE_TEXT) delete requestBody.options;
      const response = await TestService.updateMCQTest(testId, requestBody);
      if (response?.question?._id && response?.question?._id !== questionId)
        navigate(`/multichoice-creator/${testId}/${response.question._id}`);
      if (response?.question) {
        setQuestion({ ...question, ...response.question });
      }
      if (response?.options?.length) {
        let updatedOptions = _.indexBy(response.options, '_id');
        setOptions(
          options.map((option) => {
            return { ...option, ...updatedOptions[option._id] };
          })
        );
      }
      if (response?.paragraph) setParagraph(response.paragraph);
      setIsTestEdited(false);
      toast.success('Test updated successfully');
    } catch (error) {
      toast.error(error?.message ?? error ?? 'Error saving question');
    } finally {
      setIsSavingQuestion(false);
    }
  };

  return (
    <>
      {deleteModalInfo && (
        <YesNoModal
          modalInfo={deleteModalInfo}
          confirm={async (isConfirmed) => {
            if (isConfirmed) await deleteQuestionOptionAndParagraph(testId, deleteModalInfo.id, deleteModalInfo.type);
            setDeleteModalInfo(undefined);
          }}
        />
      )}
      <QuestionsContainer>
        {questions?.map((q, index) => (
          <QuestionsSelectionButton
            key={q._id}
            isActive={q._id === question._id}
            style={{ background: getColor(q), borderColor: q._id !== question._id && getColor(q) }}
            href={`/multichoice-creator/${testId}/${q._id}`}
          >
            {index + 1}
          </QuestionsSelectionButton>
        ))}
        <AddQuestionButton
          disabled={test?.disabledEditing}
          onClick={() => {
            if (question.new) return;
            selectCategoryRef?.current?.clearValue();
            setQuestion({ new: true, value: '', category: '', questionType: questionTypes.ONE_CORRECT_ANSWER });
            setOptions([
              { new: true, value: '', correct: false },
              { new: true, value: '', correct: false },
            ]);
            setParagraph({});
          }}
        >
          <ITag icon="fa fa-plus" color="white" />
          Add Question
        </AddQuestionButton>
      </QuestionsContainer>
      {deleting && <Spinner />}
      {!deleting && (question.new || question._id) && (
        <>
          {renderParagraphSection()}
          {renderQuestionDetails()}
          {renderCategorySection()}
          {renderQuestionTypeSection()}
          {renderQuestionAndOptions()}
          <ButtonContainer justifyContent="flex-end">
            {question.questionType !== questionTypes.FREE_TEXT && (
              <Button
                disabled={test?.disabledEditing}
                removeMargin
                padding="5px 15px"
                fontSize="14px"
                text-align="right"
                onClick={() => {
                  let copyOfOptions = JSON.parse(JSON.stringify(options));
                  copyOfOptions.push({ new: true, value: '', correct: false });
                  setOptions(copyOfOptions);
                }}
                primaryBlue
                primaryGreenBorder
              >
                <i className="fa fa-plus mr-1" />
                Add Option
              </Button>
            )}
            <Button
              disabled={test?.disabledEditing || isSavingQuestion}
              removeMargin
              padding="5px 15px"
              fontSize="14px"
              text-align="right"
              onClick={() => saveQuestionOptionsAndParagraph()}
              primaryBlue
              primaryGreenBorder
            >
              <i className="fa fa-save mr-1" />
              Save Question
            </Button>
          </ButtonContainer>
        </>
      )}
    </>
  );
});
